import React from 'react';
import {bindActionCreators} from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../../../redux/actions'; //Import your actions
import DragDrop from '../DragDrop';
import 'materialize-css';
import '../../../styles/report.scss';
import TextareaAutosize from 'react-textarea-autosize';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { encodeImageFile, isPdfFile } from '../../../utils/encodeImageFile';
import PdfAttachment from '../PdfAttachment';

// PDFs are sent to the API as-is, inside the report update.
const PDF_MAX_BYTES = 25 * 1024 * 1024;

class FileUpload extends React.Component {

  constructor() {
    super();
    this.state = {
      file: null, // base64 data
      fileName: null,
      isPdf: false,
      pdfPreviewUrl: null,
      note: '',
      converting: false,
      uploading: false, // the attachment is on its way to the API
      saved: false, // the API confirmed it; shown briefly before the modal closes
    };
  }

  componentWillUnmount() {
    this.unmounted = true;
    clearTimeout(this.closeTimer);
    this.releasePreview();
  }

  releasePreview() {
    if (this.state.pdfPreviewUrl) URL.revokeObjectURL(this.state.pdfPreviewUrl);
  }

  encodeFile = async (file) => {
    if (isPdfFile(file) && file.size > PDF_MAX_BYTES) {
      alert('That PDF is larger than 25 MB. Please attach a smaller file.');
      return;
    }

    this.setState({ converting: true });
    try {
      const dataUrl = await encodeImageFile(file);
      const isPdf = isPdfFile(file);
      this.releasePreview();
      this.setState({
        file: dataUrl,
        fileName: file.name,
        isPdf,
        // The browser's PDF viewer needs a blob URL; it won't load a data URL in a frame.
        pdfPreviewUrl: isPdf ? URL.createObjectURL(new Blob([file], { type: 'application/pdf' })) : null,
        converting: false,
      });
    } catch (error) {
      console.error('Image encode failed', error);
      this.setState({ converting: false });
      alert('Could not process that file. Please try a JPG, PNG, HEIC, or PDF file.');
    }
  }

  addImage(e) {
    e.stopPropagation();
    if (this.state.uploading || this.state.saved) return;

    let id = Date.now();

    let noteObj = {
      id: id,
      report_id: this.props.report?.id,
      note_type: 'image',
      content: this.state.note,
      component_id: this.props.component.id,
      image: this.state.file,
      image_name: this.state.fileName,
      image_type: this.state.isPdf ? 'application/pdf' : undefined,
      iAm: 'image',
      breadcrumbs: this.props.saveNode ? undefined : (
        this.props.component.id === '-1' ? 
        `${this.props.report.id}.components.log.nodes.${new Date().toLocaleString().split(',')[0]}.${id}` :
        `${this.props.report.id}.components.${this.props.component.id}.notes.${id}`
      )
    };

    const persist = (node, storage, onComplete) => {
      if (this.props.saveNode) {
        return this.props.saveNode(node, storage, onComplete);
      }
      return this.props.updateReport(this.buildScaffold(node), storage, onComplete);
    };

    // The modal closes itself once the API confirms. If the request fails
    // instead, the button goes back to normal so the user can try again.
    this.setState({ uploading: true });
    // Once the API confirms, the button shows "Saved" for a moment before the modal closes.
    const saved = () => {
      if (this.unmounted) return;
      this.setState({ uploading: false, saved: true });
      this.closeTimer = setTimeout(() => this.props.close(), 900);
    };
    const reset = () => { if (!this.unmounted) this.setState({ uploading: false }); };
    Promise.resolve()
      .then(() => persist(noteObj, {fridge: true, freezer: this.props.authToken}, saved))
      .then(reset, reset);
  }

  buildScaffold(node) {
    let breadcrumbs = node.breadcrumbs.split('.');
    let reportId = breadcrumbs.reverse().pop();
    let reportScaffold = {
      id: reportId,
      ...breadcrumbs.reduce((val, key) => ({[key]: val}), {...node})
    };
   
    return reportScaffold;
  }

  handleInputKeypress(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.addImage(event);
    }
  }

  contextLabel() {
    if (`${this.props.component?.id}` === '-1') return 'Service notebook';
    return this.props.component?.name || null;
  }

  clearImage(e) {
    e.stopPropagation();
    this.releasePreview();
    this.setState({ file: null, fileName: null, isPdf: false, pdfPreviewUrl: null });
  }

  render() {
    const hasImage = !!this.state.file;
    const converting = this.state.converting;
    const context = this.contextLabel();

    return (
      <div
        className={`report-modal image-upload${hasImage ? ' has-image' : ''}${this.props.embedded ? ' is-embedded' : ''}`}
        onClick={this.props.embedded ? undefined : () => this.props.close()}
      >
        <div className="image-upload-card" onClick={(e) => e.stopPropagation()}>
          <div className="image-upload-head">
            <div className="image-upload-heading">
              <span className="image-upload-title">Attach file</span>
              {context ? <span className="image-upload-context">{context}</span> : null}
            </div>
            <button
              type="button"
              className="image-upload-close"
              aria-label="Close"
              onClick={() => this.props.close()}
            >
              <CloseRoundedIcon />
            </button>
          </div>

          {converting ? (
            <div className="image-upload-drop is-busy">
              <AttachFileIcon className="image-upload-drop-icon" />
              <p>Preparing file…</p>
            </div>
          ) : hasImage && this.state.isPdf ? (
            <div className="image-upload-preview-wrap is-pdf">
              <PdfAttachment url={this.state.file} name={this.state.fileName} />
              <iframe
                className="pdf-preview-frame"
                title={`Preview of ${this.state.fileName}`}
                src={`${this.state.pdfPreviewUrl}#toolbar=0&navpanes=0`}
              />
            </div>
          ) : hasImage ? (
            <div className="image-upload-preview-wrap">
              <img className="file-upload-preview" src={this.state.file} alt="uploaded" />
            </div>
          ) : (
            <div className="image-upload-drop">
              <DragDrop upload={(file) => this.encodeFile(file)} />
            </div>
          )}

          {hasImage ? (
            <>
              <div className="image-upload-body">
                <TextareaAutosize
                  className="image-upload-input"
                  placeholder="Add a caption…"
                  autoFocus
                  minRows={2}
                  maxRows={6}
                  onKeyDown={(e) => this.handleInputKeypress(e)}
                  onChange={(e) => this.setState({ note: e.target.value })}
                  onFocus={function (e) {
                    var val = e.target.value;
                    e.target.value = '';
                    e.target.value = val;
                  }}
                  value={this.state.note}
                />
              </div>
              <div className="image-upload-foot">
                <button
                  type="button"
                  className="image-upload-button ghost"
                  disabled={this.state.uploading || this.state.saved}
                  onClick={(e) => this.clearImage(e)}
                >
                  Replace
                </button>
                <button
                  type="button"
                  className={`image-upload-button primary${this.state.uploading ? ' is-uploading' : ''}${this.state.saved ? ' is-saved' : ''}`}
                  disabled={this.state.uploading || this.state.saved}
                  aria-busy={this.state.uploading}
                  onClick={(e) => this.addImage(e)}
                >
                  {this.state.uploading ? (
                    <>
                      <span className="saving-flash shown" aria-hidden="true" />
                      ...Uploading
                    </>
                  ) : this.state.saved ? (
                    <>
                      <CheckRoundedIcon />
                      Saved
                    </>
                  ) : (
                    <>
                      <CheckRoundedIcon />
                      Attach
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="image-upload-foot">
              <span className="image-upload-hint">JPG, PNG, HEIC, or PDF</span>
            </div>
          )}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state, props) {
  return {
    authToken: state.authToken,
    report: state.report,
    tests: state.testFields
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(FileUpload);
